/** One delivery target of a subscription, as returned in `alertTargets` by the v2 API. */
export type AlertTarget = {
  channel: string;
  details?: {
    email?: string;
    httpEndpoint?: string;
    deliveryFrequency?: string;
    dayOfWeek?: number;
  };
};

/** Delivery channels; values match the `channel` strings used by the subscriptions API and multiplexer. */
export const CHANNELS = {
  EMAIL: 'email',
  HTTP: 'http',
  DIGEST: 'digest',
  INAPP: 'inapp',
} as const;

// Digest subscriptions use cron-style day numbering: 0 = Sunday through 6 = Saturday.
const CRON_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Returns the human-readable destination or schedule for one target, or '' when there is none.
 * A channel without a case here (e.g. newly added in the API) shows its label only; add a case
 * to describe its details.
 * @param target A single alert target.
 */
export const describeAlertTarget = ({ channel, details = {} }: AlertTarget): string => {
  switch (channel) {
    case CHANNELS.EMAIL:
      return details.email ?? '';
    case CHANNELS.HTTP:
      return details.httpEndpoint ?? '';
    case CHANNELS.DIGEST: {
      const schedule =
        details.deliveryFrequency === 'WEEKLY' && details.dayOfWeek !== undefined
          ? `WEEKLY (${CRON_DAY_NAMES[details.dayOfWeek] ?? details.dayOfWeek})`
          : details.deliveryFrequency;

      return [schedule, details.email].filter(Boolean).join(' · ');
    }
    case CHANNELS.INAPP:
      return 'Notification Center';
    default:
      return '';
  }
};

/**
 * Returns the target channel names in the order the API returned them, comma-separated.
 * @param targets The subscription's `alertTargets`.
 */
export const formatChannelNames = (targets?: AlertTarget[] | null): string =>
  (targets ?? []).map(({ channel }) => channel).join(', ');
